import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect } from "react";
import { AlenDevice } from "src/interfaces/IAlenDevice";
import { FirmwareUpdateDetails } from "src/services/alen/firmwareUpdate";
import {
  INFO_BLUE,
  MESSAGING_ERROR,
  MESSAGING_SUCCESS,
  MESSAGING_WARNING,
} from "src/styles";
import useFirmwareUpdateContext from "./FirmwareUpdate/FirmwareUpdateContext";
import { AlenFirmwareUpdateStatus } from "./FirmwareUpdate/types";
import useFirmwareUpdateProgress from "./FirmwareUpdate/useFirmwareUpdateProgress";

const getStatusTitle = (status: AlenFirmwareUpdateStatus) => {
  switch (status) {
    case AlenFirmwareUpdateStatus.UpdateCompleted:
      return "Completed";
    case AlenFirmwareUpdateStatus.UpdateInProgress:
      return "Updating firmware";
    case AlenFirmwareUpdateStatus.UpdateError:
      return "Update Failed";
    case AlenFirmwareUpdateStatus.UpdateAvailable:
      return "Update Available";
    case AlenFirmwareUpdateStatus.NoUpdate:
      return "No Update Available";
    default:
      return "Unknown Status";
  }
};

const getStatusColor = (status: AlenFirmwareUpdateStatus) => {
  switch (status) {
    case AlenFirmwareUpdateStatus.UpdateCompleted:
      return MESSAGING_SUCCESS;
    case AlenFirmwareUpdateStatus.UpdateInProgress:
      return INFO_BLUE;
    case AlenFirmwareUpdateStatus.UpdateError:
      return MESSAGING_ERROR;
    case AlenFirmwareUpdateStatus.UpdateAvailable:
      return MESSAGING_WARNING;
    case AlenFirmwareUpdateStatus.NoUpdate:
      return "#000";
    default:
      return "#000";
  }
};

const FirmwareUpdateProgressModal = ({
  device,
  firmwareUpdateDetails,
}: {
  device: AlenDevice;
  firmwareUpdateDetails: FirmwareUpdateDetails;
}) => {
  const status = useFirmwareUpdateProgress(
    device.id,
    firmwareUpdateDetails?.channel
  );

  const { setUpdateProgress, setSelectedDeviceFirmware } =
    useFirmwareUpdateContext();

  useEffect(() => {
    const unloadCallback = (event: BeforeUnloadEvent) => {
      if (status !== AlenFirmwareUpdateStatus.UpdateInProgress) {
        return;
      }
      event.preventDefault();
    };

    window.addEventListener("beforeunload", unloadCallback);
    return () => window.removeEventListener("beforeunload", unloadCallback);
  }, [status]);

  return (
    <Dialog fullWidth maxWidth="xs" open onClose={() => {}}>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="subtitle1">{device.name}</Typography>
          <Typography variant="body1" color={getStatusColor(status)}>
            {getStatusTitle(status)}
          </Typography>
          {[
            AlenFirmwareUpdateStatus.UpdateInProgress,
            AlenFirmwareUpdateStatus.UpdateAvailable,
          ].includes(status) && <LinearProgress />}
        </Stack>
      </DialogContent>
      {![
        AlenFirmwareUpdateStatus.UpdateInProgress,
        AlenFirmwareUpdateStatus.UpdateAvailable,
      ].includes(status) && (
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => {
              setSelectedDeviceFirmware(null);
              setUpdateProgress(null);
            }}
          >
            Done
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default function FirmwareUpdateProgress() {
  const { updateProgress, selectedDeviceFirmware } = useFirmwareUpdateContext();
  if (!updateProgress) return null;
  if (!selectedDeviceFirmware) return null;
  if (!selectedDeviceFirmware.firmware) return null;
  return (
    <FirmwareUpdateProgressModal
      device={selectedDeviceFirmware.device}
      firmwareUpdateDetails={selectedDeviceFirmware.firmware}
    />
  );
}
